import React from 'react';
import Container from '../layout/Container';
import Eyebrow from '../ui/Eyebrow';
import SectionHeading from '../ui/SectionHeading';
import CountUp from '../ui/CountUp';
import { colors, font } from '../../lib/tokens';

const ANNOUNCEMENT_URL =
  'https://island.is/s/rannis/frett/fulltruar-49-verkefna-fa-styrk-ad-andvirdi-803-milljona-krona-i-voruthlutun-taeknithrounarsjods';

const STEPS = [
  {
    n: '01',
    title: 'Dictate',
    body: 'Short dictations are transcribed right away, instead of sitting in a queue for days.',
    accent: false,
  },
  {
    n: '02',
    title: 'Code',
    body: 'KatlaCode proposes NCSP procedure codes and ICD-10 diagnoses straight from the note.',
    accent: false,
  },
  {
    n: '03',
    title: 'Draft',
    body: 'A discharge summary is drafted from lab results, clinical notes, and medication records.',
    accent: false,
  },
  {
    n: '04',
    title: 'Approve',
    body: 'A physician reviews and approves every output before anything reaches the patient record.',
    accent: true,
  },
];

const ASSURANCES = [
  'Physician-approved by design. KatlaCode never finalises a discharge on its own.',
  'Built on our validated ICD-10 coding system, already live in the hospital.',
];

// Draws itself in once the assurances box is revealed (`.kc-draw`).
function Check({ color, delayMs }: { color: string; delayMs: number }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      aria-hidden
      className="kc-draw"
      style={{ flexShrink: 0, marginTop: 4, '--kc-draw-d': `${delayMs}ms` } as React.CSSProperties}
    >
      <path d="M2 7l3.5 3.5L12 3" pathLength={1} stroke={color} strokeWidth="1.6" fill="none" strokeLinecap="round" />
    </svg>
  );
}

export default function DischargeCopilotSection() {
  return (
    <section
      id="discharge"
      className="kc-section"
      style={{
        background: colors.surface,
        borderTop: `1px solid ${colors.hair}`,
      }}
    >
      <Container>
        {/* Header: intro (left) + headline stat (right) */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1.05fr] gap-12 md:gap-24">
          <div data-reveal>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Eyebrow>In development</Eyebrow>
              <span
                style={{
                  padding: '3px 9px',
                  borderRadius: 999,
                  background: colors.blueSoft,
                  color: colors.blue,
                  fontFamily: font.mono,
                  fontSize: 10.5,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                  fontWeight: 500,
                }}
              >
                Coming soon
              </span>
            </div>
            <SectionHeading style={{ marginBottom: 24 }}>
              An AI Discharge Copilot
              {' '}<br />
              for physicians.
            </SectionHeading>
            <p style={{ color: colors.muted, fontSize: 16, lineHeight: 1.65, maxWidth: 440, margin: 0 }}>
              KatlaCode is building a copilot that automates part of the time-consuming documentation
              behind every hospital discharge. A physician stays in control of the result.
            </p>

            {/* Grant tie-in */}
            <a
              href={ANNOUNCEMENT_URL}
              target="_blank"
              rel="noreferrer noopener"
              className="kc-news-link"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 28,
                fontFamily: font.mono,
                fontSize: 11.5,
                letterSpacing: 0.6,
                textTransform: 'uppercase',
                color: colors.muted,
                textDecoration: 'none',
              }}
            >
              <span style={{ color: colors.blue }}>↳</span>
              Backed by a 2026 Technology Development Fund grant
            </a>
          </div>

          {/* Headline stat + today's burden */}
          <div
            data-reveal
            style={{
              '--d': '80ms',
              background: colors.bg,
              border: `1px solid ${colors.hair}`,
              borderRadius: 16,
              padding: 36,
            } as React.CSSProperties}
          >
            <div
              style={{
                fontFamily: font.sans,
                fontSize: 'clamp(52px, 7vw, 72px)',
                fontWeight: 500,
                letterSpacing: -3,
                lineHeight: 0.95,
                color: colors.ink,
              }}
            >
              <CountUp to={25000} duration={1800} />
              <span style={{ fontSize: 20, letterSpacing: -0.5, color: colors.muted, fontWeight: 500 }}>
                {' '}
                hrs / yr
              </span>
            </div>
            <div style={{ color: colors.ink2, fontSize: 15, marginTop: 14, fontWeight: 500 }}>
              Estimated physician time the copilot can save each year at Landspítali alone.
            </div>
            <p
              style={{
                color: colors.muted,
                fontSize: 14,
                lineHeight: 1.65,
                margin: '16px 0 0',
                paddingTop: 16,
                borderTop: `1px solid ${colors.hair}`,
              }}
            >
              Today, dictated reports can wait days to be transcribed. A physician then reviews the
              text, assigns NCSP and ICD-10 codes, and assembles the summary from labs, notes, and
              medications. Delays carry real patient-safety risk.
            </p>
          </div>
        </div>

        {/* Workflow */}
        <div
          data-reveal
          style={{
            fontFamily: font.mono,
            fontSize: 11,
            letterSpacing: 1,
            textTransform: 'uppercase',
            color: colors.dim,
            marginTop: 72,
            marginBottom: 28,
          }}
        >
          How the copilot works
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
          {STEPS.map((s, i) => (
            <div
              key={s.n}
              data-reveal
              className="kc-rule"
              style={{
                '--d': `${i * 90}ms`,
                '--kc-rule': s.accent ? colors.blue : colors.ink,
                paddingTop: 18,
              } as React.CSSProperties}
            >
              {/* Fixed height so the "YOU" pill doesn't push step 04 lower */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  height: 18,
                  gap: 8,
                  fontFamily: font.mono,
                  fontSize: 11,
                  color: s.accent ? colors.blue : colors.muted,
                  letterSpacing: 1,
                }}
              >
                STEP {s.n}
                {s.accent && (
                  <span
                    style={{
                      fontSize: 9.5,
                      padding: '2px 7px',
                      borderRadius: 999,
                      background: colors.blueSoft,
                      color: colors.blue,
                      letterSpacing: 0.6,
                    }}
                  >
                    YOU
                  </span>
                )}
              </div>
              <h3
                style={{
                  fontFamily: font.sans,
                  fontSize: 20,
                  fontWeight: 500,
                  letterSpacing: -0.4,
                  marginTop: 14,
                  marginBottom: 10,
                  color: colors.ink,
                }}
              >
                {s.title}
              </h3>
              <p style={{ color: colors.muted, fontSize: 14, lineHeight: 1.6, margin: 0 }}>{s.body}</p>
            </div>
          ))}
        </div>

        {/* Assurances */}
        <div
          data-reveal
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 14,
            marginTop: 48,
            background: colors.blueSoft,
            border: `1px solid ${colors.hair}`,
            borderRadius: 14,
            padding: '24px 28px',
          }}
        >
          {ASSURANCES.map((a, i) => (
            <div key={a} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <Check color={colors.blue} delayMs={300 + i * 160} />
              <span style={{ color: colors.ink2, fontSize: 14.5, lineHeight: 1.55 }}>{a}</span>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}
